import { UseMutateFunction } from '@taxdown/zeno-sama-query';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import { TaxReturnTypes } from 'Containers/Result/utils';
import {
  ResponseType,
  ResultType,
  ResultTypes,
  SubmitInfoFilingType,
  SubmitResultType,
} from 'Types/common';

const ZERO_AMOUNT = 0;

export const handleSubmit = (
  data: object,
  mutate: UseMutateFunction<unknown, unknown, unknown, unknown>,
  submitDeclaration: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>
): void => {
  mutate(data, {
    onSuccess: () => {
      submitDeclaration({});
    },
  });
};

export const handleToPay = (
  installments: number,
  clabeAccount: string,
  mutate: UseMutateFunction<unknown, unknown, unknown, unknown>,
  submitDeclaration: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>,
  dataSubmitInfo?: SubmitInfoFilingType,
  isCompensation?: boolean
): void => {
  const dataInfo = {
    installments,
    CLABE: dataSubmitInfo?.CLABE || clabeAccount,
    payWithExternalService: isCompensation,
  };
  handleSubmit(dataInfo, mutate, submitDeclaration);
};

export const handleClabeAccount = (
  clabeAccount: string,
  mutate: UseMutateFunction<unknown, unknown, unknown, unknown>,
  submitDeclaration: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>,
  isCompensation?: boolean
): void => {
  handleSubmit(
    { CLABE: clabeAccount, payWithExternalService: isCompensation },
    mutate,
    submitDeclaration
  );
};

export const handleEqual = (
  submitDeclaration: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>
): void => {
  submitDeclaration({});
};

export const handleFlexPlan = (
  onChangeStep: (step: number) => void,
  mutate: UseMutateFunction<unknown, unknown, unknown, unknown>,
  clabeAccount: string,
  isCompensation?: boolean,
  submitDeclaration?: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>
): void => {
  mutate(
    {
      CLABE: clabeAccount,
      payWithExternalService: isCompensation,
    },
    {
      onSuccess: () => {
        if (submitDeclaration) {
          submitDeclaration({});
        } else {
          onChangeStep(7);
        }
      },
    }
  );
};

export const handleFlexLitePlan = (
  submitDeclarationCrossSell: UseMutateFunction<
    ResponseType<SubmitResultType>,
    unknown,
    unknown,
    unknown
  >
): void => {
  submitDeclarationCrossSell({});
};

export const handleOtherPlans = (
  info: ResultType,
  clabeAccount: string,
  installments: number,
  mutate: UseMutateFunction<unknown, unknown, unknown, unknown>,
  submitDeclaration: UseMutateFunction<ResponseType<SubmitResultType>, unknown, unknown, unknown>,
  dataSubmitInfo?: SubmitInfoFilingType,
  isCompensation?: boolean
): void => {
  if (info?.taxReturnType === ResultTypes.toPay) {
    handleToPay(
      installments,
      clabeAccount,
      mutate,
      submitDeclaration,
      dataSubmitInfo,
      isCompensation
    );
  } else if (clabeAccount) {
    handleClabeAccount(clabeAccount, mutate, submitDeclaration, isCompensation);
  } else if (info?.taxReturnType === ResultTypes.equal) {
    handleEqual(submitDeclaration);
  }
};

export const calculateCompensationInfo = (
  previousYearData?: PueblaPreviousYearsResponse,
  info?: { compensation?: boolean; taxReturnType?: string; taxReturnResult?: number }
): { taxReturnType: TaxReturnTypes; taxReturnResult: number } => {
  const amountPending = Math.abs(previousYearData?.amountPending ?? 0);
  const taxResult = Math.abs(info?.taxReturnResult ?? 0);
  const isCompensation = info?.compensation ?? false;

  if (!isCompensation) {
    return {
      taxReturnType: (info?.taxReturnType as TaxReturnTypes) ?? TaxReturnTypes.EQUAL,
      taxReturnResult: taxResult,
    };
  }

  const isCompensationAndPayInfo = amountPending < taxResult;
  const isZeroAmount = amountPending === taxResult;
  const taxReturnType = isCompensationAndPayInfo
    ? TaxReturnTypes.TO_PAY
    : isZeroAmount
      ? TaxReturnTypes.EQUAL
      : (info?.taxReturnType as TaxReturnTypes);
  const taxReturnResult = isCompensationAndPayInfo
    ? amountPending - taxResult
    : isZeroAmount
      ? ZERO_AMOUNT
      : taxResult;
  return {
    taxReturnType,
    taxReturnResult: Math.abs(taxReturnResult),
  };
};
